import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Lock } from "lucide-react";
import { Panel, Chip, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BrokerSummary } from "@/components/levels/LivePanel";
import { api, errMsg, fmt, gbp, pnlClass, ldnTime, cur } from "@/lib/api";

const LABELS = { credentials_ok: "MT5 credentials verified and connected", engine_on: "Engine ON", mode_auto: "Mode is auto", armed_for_live: "Arm for LIVE toggled", symbols_resolved: "Brent and WTI symbols resolved" };

export function OpenTrade({ s, onChange }) {
  const t = s.open_trades?.[0];
  const force = async (live) => {
    if (!window.confirm(live ? "Close both live legs and the paper trade now?" : "Close the paper trade now?")) return;
    try { await api.post("/oil/force-close", { live }); toast.success("Closed"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ap = s.alert_position;
  return (
    <Panel title="Open trade" testid="oil-open-trade" right={s.multi_trade_alarm && <Chip status="TRIGGERED" label="More than one open trade" testid="oil-multi-trade-alarm" />}>
      {!t ? (
        <p className="text-sm text-slate-500" data-testid="oil-no-open-trade">{ap ? `Alert mode is tracking a ${ap.dir > 0 ? "LONG" : "SHORT"} SPREAD signal from ${fmt(ap.entry_spread, 3)}. No trades are placed in alert mode.` : "Flat. No open spread trade."}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Chip status={t.dir > 0 ? "LONG" : "SHORT"} label={`${t.side} spread`} />
            <span className="num text-sm text-slate-300">Entry {fmt(t.entry_spread, 3)} at {ldnTime(t.opened_at)}, {cur()}{fmt(t.stake)}/pt per leg</span>
            {t.live && <Chip status={t.live.status === "open" ? "LIVE" : "WARNING"} label={`live ${t.live.status}`} />}
          </div>
          <table className="w-full"><thead><tr><Th>Leg</Th><Th>Side</Th><Th>Symbol</Th><Th>Entry</Th><Th>Live ticket</Th></tr></thead>
            <tbody>
              <tr><Td>Brent</Td><Td>{t.dir > 0 ? "Buy" : "Sell"}</Td><Td className="num">{t.brent_symbol}</Td><Td className="num">{fmt(t.entry_brent)}</Td><Td className="num">{t.live?.brent?.position_id || "-"}</Td></tr>
              <tr><Td>WTI</Td><Td>{t.dir > 0 ? "Sell" : "Buy"}</Td><Td className="num">{t.wti_symbol}</Td><Td className="num">{fmt(t.entry_wti)}</Td><Td className="num">{t.live?.wti?.position_id || "-"}</Td></tr>
            </tbody>
          </table>
          <div className="flex items-center gap-4">
            <div className={`num text-2xl font-bold ${pnlClass(t.live_pnl)}`} data-testid="oil-open-pnl">{gbp(t.live_pnl)}</div>
            <div className={`num text-sm ${pnlClass(t.live_points)}`}>{fmt(t.live_points)} pts</div>
            <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800"><div className={`h-full ${t.live_pnl >= 0 ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, Math.abs(t.live_pnl || 0) / Math.max(1, s.calc.max_risk_gbp) * 100)}%` }} /></div>
          </div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="oil-force-close-paper" size="sm" variant="secondary" onClick={() => force(false)}>Force close paper</Button>
        {s.is_admin && <Button data-testid="oil-force-close-live" size="sm" variant="destructive" onClick={() => force(true)}>Force close live</Button>}
      </div>
    </Panel>
  );
}

export function OilLive({ s, onChange }) {
  const c = s.checklist;
  const arm = async (v) => {
    try { await api.put("/oil/config", { live_trading_enabled: v }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="MT5 broker and live arm checklist" testid="oil-live-card" right={<Chip status={c.ready_for_live ? "ARMED" : "PAPER"} label={c.ready_for_live ? "ready_for_live" : "PAPER"} testid="oil-ready-badge" />}>
      <BrokerSummary broker={s.broker} />
      <ul className="mt-4 space-y-2" data-testid="oil-arm-checklist">
        {Object.entries(LABELS).map(([k, l]) => (
          <li key={k} className="flex items-center gap-2 text-sm" data-testid={`oil-check-${k}`}>
            {c[k] ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}<span className={c[k] ? "text-slate-200" : "text-rose-200/80"}>{l}</span>
          </li>
        ))}
      </ul>
      {s.is_admin ? (
        <label className="mt-4 flex items-center gap-2 text-sm text-amber-300"><Switch data-testid="oil-arm-live-toggle" checked={s.config.live_trading_enabled} onCheckedChange={arm} />Arm for LIVE</label>
      ) : <p className="mt-4 text-xs text-slate-500">Only an admin can arm live trading.</p>}
      <p className="mt-3 text-xs text-slate-500">Live entries send two market orders together. If the second leg fails the first is closed straight away and logged as a leg mismatch.</p>
    </Panel>
  );
}

export function SymbolLock({ s, onChange }) {
  const [f, setF] = useState({ brent: "", wti: "", locked: false });
  useEffect(() => { setF({ brent: s.config.brent_symbol || s.symbols.brent || "", wti: s.config.wti_symbol || s.symbols.wti || "", locked: s.config.symbol_lock }); }, [s.config.brent_symbol, s.config.wti_symbol, s.config.symbol_lock, s.symbols.brent, s.symbols.wti]);
  const save = async () => {
    try { await api.put("/oil/symbol-lock", { brent_symbol: f.brent, wti_symbol: f.wti, locked: f.locked }); toast.success("Symbols saved"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Symbol lock" testid="oil-symbol-lock" right={<Lock className="h-4 w-4 text-slate-500" />}>
      <div className="num mb-3 text-xs text-slate-400">In use: Brent {s.symbols.brent || "unresolved"}, WTI {s.symbols.wti || "unresolved"}. Leave blank to auto-find them on your MT5 account.</div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-400">Brent<Input disabled={!s.is_admin} data-testid="oil-brent-symbol-input" value={f.brent} onChange={(e) => setF({ ...f, brent: e.target.value })} className="num mt-1 h-9 w-32 bg-slate-950" /></label>
        <label className="text-xs text-slate-400">WTI<Input disabled={!s.is_admin} data-testid="oil-wti-symbol-input" value={f.wti} onChange={(e) => setF({ ...f, wti: e.target.value })} className="num mt-1 h-9 w-32 bg-slate-950" /></label>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-300"><Switch disabled={!s.is_admin} data-testid="oil-symbol-lock-switch" checked={f.locked} onCheckedChange={(v) => setF({ ...f, locked: v })} />Pin</label>
        {s.is_admin && <Button data-testid="oil-symbol-lock-save" size="sm" variant="secondary" onClick={save}>Save</Button>}
      </div>
    </Panel>
  );
}
